import "../styling/scss/App.scss";

import { useState, useEffect, useMemo } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { PlusIcon, TimesIcon } from "@simplex/sx-icons";

import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { LoadCaseColor } from "./SXProjectInput";
import { PopUpModal } from "./modals/common/PopUp";

import { loadcases_getVisualsColor } from "../helpers/Colors";
import { AlertMessage } from "../helpers/AlertMessage";
import { createDefaultLoadcase } from "../helpers/defaultObject";
import { getMtrl } from "@common/helpers/protoGen";
import { ng } from "@common/ngds";

import {
  LOAD_CASE_TYPES_FOR_DROPDOWN_G1,
  LOAD_CASE_TYPES_FOR_DROPDOWN_G2,
  LOADCASE_CATEGORIES_FOR_DROPDOWN_G1,
  LOADCASE_CATEGORIES_FOR_DROPDOWN_G2,
  DURATION_CLASSES_FOR_DROPDOWN,
} from "@common/helpers/funlib";

import { useAppSelector, useAppDispatch } from "../app/hooks";
import { selectUser } from "../features/user/userSlice";
import { useProject } from "../features/projects/projectSlice";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";

///////////////////////////////////////////////////////////////////////////////////////////////////

// Constants

const maxLoadcaseNameLength = 24;

interface SXProjectInputLoadCasesProps {
  setWindowIsInfocus?: (value: boolean) => void;
  setToggleLoadcombEd?: (value: boolean) => void;
  setComboForEdit?: (value: ng.loadcombination.IData) => void;
  factorUpdate?: boolean;
  project?: any; //  ng.project.Data;
  setFactorUpdate?: (value: boolean) => void;
  setToggleLCOGenerator?: (value: boolean) => void;
  pushChanges?: (c: any) => void;
  handleDoubleClick?: any;
}

export const SXProjectInputLoadCases = (props: SXProjectInputLoadCasesProps) => {
  // --- Contexts ---
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const pp = useProject();
  const {
    project: selProject,
    addLoadcaseRedux,
    deleteLoadcaseRedux,
    setLoadCaseName,
    setLoadcaseType,
    setLoadcaseCategory,
    setLoadcaseDuration,
    setLoadCaseVisuals,
  } = pp;

  // --- Data from Redux state ---
  const projDataJson = selProject?.data?.data?.input;

  const { loading, loadcases, loadcombinations, loads, beam, na, gen } = useMemo(() => {
    const input = projDataJson ? ng.input.Data.fromObject(projDataJson) : null;
    const structure = input?.structures?.[0] ?? null;
    const loading = structure?.loading ?? null;
    const loadcases = loading?.loadcases ?? [];
    const loadcombinations = loading?.combinations ?? [];
    const loads = loading?.loads ?? [];
    const element = structure?.elements?.[0] ?? null;
    const beam = element?.getBeam() ?? null;

    let na = ng.eurocode.Annex.ANNEX_UNSPECIFIED;
    let gen = ng.eurocode.Generation.GENERATION_1;
    const code = input?.code;
    if (code?.type === "ec") {
      na = code.ec?.nationalAnnex ?? ng.eurocode.Annex.ANNEX_UNSPECIFIED;
      gen = code.ec?.generation || ng.eurocode.Generation.GENERATION_1;
    }

    return {
      loading,
      loadcases,
      loadcombinations,
      loads,
      beam,
      na,
      gen,
    };
  }, [projDataJson]);

  // --- Local UI state only ---
  const [popUp, setPopUp] = useState(false);
  const [delLCA, setDelLCA] = useState<ng.loadcase.IData | null>(null);
  const [timber, setTimber] = useState(false);

  // --- Effects ---

  // --- project.data as ng.input.IData might be wrong choice here, we'll see... ---
  useEffect(() => {
    if (beam && beam.mtrlGuid && projDataJson) {
      const input = ng.input.Data.fromObject(projDataJson);
      const mtrl = getMtrl(beam.mtrlGuid, input) ?? new ng.material.Data();
      setTimber(mtrl.type === ng.material.Mtrl.MTRL_TIMBER);
    } else {
      setTimber(false);
    }
  }, [beam, projDataJson]);

  //---------------------------------------------------------------------------------

  // --- Load case helper functions for add, update, and delete ---

  const checkBeforeDeleteLoadcase = async (lca: ng.loadcase.IData) => {
    let okToDelete = true;
    let msg = "";
    // Check if requested loadcase is referenced in any load.
    // If so, deny deletion
    loads?.forEach((load: ng.load.IData) => {
      if (load.lcaseGuid === lca.id?.guid) {
        okToDelete = false;
        if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_POINT) {
          msg = t("MSG_loadcase_delete_pointload_.label");
        } else if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_LINE) {
          msg = t("MSG_loadcase_delete_lineload_.label");
        }
      }
    });

    if (!okToDelete) {
      setPopUp(true);
    } else {
      if (lca.id && lca.id.guid) {
        await deleteLoadcase(lca.id.guid, true);
      }
    }
  };

  const addLoadcase = async () => {
    let newLoadCase = createDefaultLoadcase();

    if (newLoadCase && newLoadCase.id && newLoadCase.id.guid) {
      // Find a unique name for the new load case on the format "LCA #"
      const existingNames = new Set(loadcases.map((lc) => lc.id?.name));
      let counter = 1;
      do {
        newLoadCase.id.name = `LCA ${loadcases.length + counter}`;
        counter++;
      } while (existingNames.has(newLoadCase.id.name));
    }

    const guid = newLoadCase.id?.guid;

    if (!guid) {
      console.log("ERROR: Failed to create a guid for the new load case");
      return;
    }

    const json = ng.loadcase.Data.toObject(newLoadCase);
    if (!json) {
      console.log("ERROR: Failed to serialize the new load case to JSON");
      return;
    }

    await addLoadcaseRedux(json);
  };

  const deleteLoadcase = async (guid: string, ok: boolean) => {
    if (ok) {
      await deleteLoadcaseRedux(guid);
    }
  };

  const updateLoadcaseName = (guid: string, name: string) => {
    setLoadCaseName(guid, name);
  };

  const updateLoadcaseType = async (guid: string, type: ng.loadcase.Type) => {
    if (!selProject) return;
    setLoadcaseType(guid, type);
    if (selProject.data) {
      // Build visuals from a project snapshot that already includes the new type.
      const projectSnapshot = ng.frontend.column.Project.fromObject(selProject.data);
      const targetLoadcase = projectSnapshot.data?.input?.structures?.[0]?.loading?.loadcases?.find(
        (lc) => lc.id?.guid === guid,
      );
      if (targetLoadcase) {
        targetLoadcase.type = type;
      }

      const newLoadcasesVisuals = loadcases_getVisualsColor(projectSnapshot);
      // Update visuals in Redux
      setLoadCaseVisuals(newLoadcasesVisuals.map(ng.frontend.column.LoadCaseVisuals.toObject).filter((x) => x != null));
    }

    if (props.factorUpdate) {
      await pp.generateLoadCombs();
    }
  };

  const updateLoadcaseDuration = async (guid: string, type: ng.loadcase.DurationClass) => {
    await setLoadcaseDuration(guid, type);
  };

  const updateLoadcaseCategory = async (guid: string, category: ng.loadcase.Category) => {
    await setLoadcaseCategory(guid, category);
  };

  // --- React response ---

  return (
    <>
      <PopUpModal
        show={popUp}
        question={"MSG_delete_loadcase_"}
        onClose={async (ret) => {
          try {
            if (delLCA != null && delLCA.id != null && delLCA.id.guid != null) {
              await deleteLoadcase(delLCA.id.guid, ret);
            }

            setPopUp(false);
          } catch (x) {
            report_exception(t, x);
          }
        }}
      />

      {/* -------------------- LOAD CASE --------------------- */}
      <Container className="sx-table-container" style={{ paddingTop: 20 }}>
        <Table hover size="sm" className="main-sx-table">
          {/* TABLE HEADER */}
          <thead className="table-header-bg">
            <tr className="table-header align-items-center">
              <th>{t("name_.label")}</th>

              <th>{t("loadcase_type_.label")}</th>

              {timber && <th>{t("loadcase_duration_.label")}</th>}

              <th>{t("loadcase_category_.label")}</th>

              <th className="table-header-bg text-center">{t("loadcase_color_.label")}</th>

              <th style={{ border: 0 }} />
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody>
            {loadcases.length > 0 &&
              loadcases.map((lca: ng.loadcase.IData, index: number) => {
                let loadcaseTypeItem;

                let self = lca.type === ng.loadcase.Type.TYPE_SELF_WEIGHT;

                if (self) {
                  loadcaseTypeItem = {
                    name: "type_self_weight_",
                    id: ng.loadcase.Type.TYPE_SELF_WEIGHT,
                  };
                } else {
                  if (gen === ng.eurocode.Generation.GENERATION_1) {
                    loadcaseTypeItem = LOAD_CASE_TYPES_FOR_DROPDOWN_G1.find((m) => m.id === lca.type);
                  } else if (gen === ng.eurocode.Generation.GENERATION_2) {
                    loadcaseTypeItem = LOAD_CASE_TYPES_FOR_DROPDOWN_G2.find((m) => m.id === lca.type);
                  }
                }

                let loadcaseCategoryItem = { id: 0, name: "" };

                if (gen === ng.eurocode.Generation.GENERATION_1) {
                  let item = LOADCASE_CATEGORIES_FOR_DROPDOWN_G1.find((m) => m.id === lca.category);
                  if (item) loadcaseCategoryItem = item;
                } else if (gen === ng.eurocode.Generation.GENERATION_2) {
                  let item = LOADCASE_CATEGORIES_FOR_DROPDOWN_G2.find((m) => m.id === lca.category);
                  if (item) loadcaseCategoryItem = item;
                }

                let imposed = lca.type === ng.loadcase.Type.TYPE_IMPOSED_LOAD;

                let loadDur = DURATION_CLASSES_FOR_DROPDOWN.find((m) => m.id === lca.durationClass);

                return (
                  <tr
                    key={lca.id?.guid ?? `loadcase_${index}`}
                    id={`loadcaseRow_${index}`}
                    className="table-body align-items-center"
                  >
                    {/* NAME */}
                    <td>
                      <SXEditField
                        tableView
                        id={self ? "selfweight_" : "lca_name_" + index}
                        disabled={self}
                        textAlign="left"
                        type="text"
                        val={lca.id?.name ?? ""}
                        onUpdate={(value: string, revertValue: () => void) => {
                          if (value.length > maxLoadcaseNameLength) {
                            let msg = t("MSG_loadcase_name_too_long_.label");

                            let context = value + " > " + maxLoadcaseNameLength.toString();

                            AlertMessage("error_", t("error_.label"), context, msg);

                            revertValue();
                            return;
                          }

                          let foundName = loadcases.find(
                            (loadcase) => loadcase.id?.name === value && loadcase.id?.guid !== lca.id?.guid,
                          );

                          if (!foundName) {
                            if (lca.id?.guid) {
                              updateLoadcaseName(lca.id.guid, value);
                            }
                          } else {
                            let msg = t("MSG_loadcase_name_already_exists_.label");

                            AlertMessage("error_", t("error_.label"), value, msg);

                            revertValue();
                          }
                        }}
                      />
                    </td>

                    {/* TYPE */}
                    <td>
                      <SXDropdown
                        tableView
                        name={"ltyp_" + index}
                        id={"ltyp_" + index}
                        dataTestId={"dropdown_ltyp"}
                        disabled={self}
                        val={loadcaseTypeItem}
                        lang=""
                        onUpdate={async (value) => {
                          try {
                            if (!value) return;

                            if (lca.id?.guid) {
                              await updateLoadcaseType(lca.id.guid, value.id);
                            }
                          } catch (x) {
                            report_exception(t, x, t("loadcase_type_.label"));
                          }
                        }}
                        list={
                          !self
                            ? gen === ng.eurocode.Generation.GENERATION_1
                              ? LOAD_CASE_TYPES_FOR_DROPDOWN_G1
                              : LOAD_CASE_TYPES_FOR_DROPDOWN_G2
                            : [
                                {
                                  name: "type_self_weight_",
                                  id: ng.loadcase.Type.TYPE_SELF_WEIGHT,
                                },
                              ]
                        }
                      />
                    </td>

                    {/* TIMBER DURATION */}
                    {timber && (
                      <td>
                        <SXDropdown
                          tableView
                          name={"ldur_" + index}
                          id={"ldur_" + index}
                          dataTestId={"dropdown_ldur"}
                          list={DURATION_CLASSES_FOR_DROPDOWN}
                          val={loadDur}
                          disabled={self}
                          lang=""
                          onUpdate={async (value) => {
                            try {
                              if (!value) return;

                              if (lca.id?.guid) {
                                await updateLoadcaseDuration(lca.id.guid, value.id);
                              }
                            } catch (x) {
                              report_exception(t, x);
                            }
                          }}
                        />
                      </td>
                    )}

                    {/* CATEGORY */}
                    <td>
                      {imposed && (
                        <SXDropdown
                          tableView
                          name={"lcat_" + index}
                          id={"lcat_" + index}
                          dataTestId={"dropdown_lcat"}
                          disabled={self}
                          lang=""
                          val={imposed ? loadcaseCategoryItem : { id: 0, name: "" }}
                          list={
                            imposed
                              ? gen === ng.eurocode.Generation.GENERATION_1
                                ? LOADCASE_CATEGORIES_FOR_DROPDOWN_G1
                                : LOADCASE_CATEGORIES_FOR_DROPDOWN_G2
                              : []
                          }
                          onUpdate={async (value) => {
                            try {
                              if (!value) return;

                              if (lca.id?.guid) {
                                await updateLoadcaseCategory(lca.id.guid, value.id);
                              }
                            } catch (x) {
                              report_exception(t, x);
                            }
                          }}
                        />
                      )}
                    </td>

                    {/* COLOR */}
                    <td className="text-center">
                      {selProject && <LoadCaseColor project={selProject.data} guid={lca?.id?.guid ?? null} />}
                    </td>

                    {/* DELETE */}
                    <td style={{ border: 0 }}>
                      {!self && (
                        <span
                          id={"deleteLoadcase_" + index}
                          className="delete-row-btn"
                          onClick={async () => {
                            try {
                              await checkBeforeDeleteLoadcase(lca);
                              setDelLCA(lca);
                            } catch (x) {
                              report_exception(t, x);
                            }
                          }}
                        >
                          <TimesIcon size={12} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>

        {/* BOTTOM ROW */}
        <Row>
          <div className="text-right" style={{ paddingRight: 10 }}>
            <span
              className="addrow add-row-btn pointer"
              id={"addLoadcase"}
              data-testid={"addLoadcase"}
              onClick={async () => {
                try {
                  await addLoadcase();
                } catch (x) {
                  report_exception(t, x);
                }
              }}
            >
              <PlusIcon size={16} />
            </span>
          </div>
        </Row>
      </Container>
    </>
  );
};
